<script lang="ts">
  import { clickOutside } from '$lib/actions/clickOutside';
  import { FOCUSABLE_ELS_SELECTOR } from '$lib/constants';
  import { computePosition, flip, offset, shift, type Placement } from '@floating-ui/dom';
  import { onMount } from 'svelte';
  import { twMerge } from 'tailwind-merge';

  export let textContent: string = '';
  export let triggerClass: string = '';
  export let popoverClass: string = '';
  export let popoverPlacement: Placement = 'bottom';

  const ANIM_DURATION = 150;

  let isOpen = false;

  let containerRef: HTMLSpanElement;
  let triggerRef: HTMLButtonElement;
  let popoverRef: HTMLElement;

  let isHovered = false;
  let isClosing = false;
  let isFocusedWithin = false;

  const animDurationClass = `duration-${ANIM_DURATION}`;

  const popoverId = `tooltip-popover-${crypto.randomUUID()}`;

  const closeTooltip = () => {
    isOpen = false;
    isClosing = true;

    setTimeout(() => {
      isClosing = false;
    }, ANIM_DURATION);
  };

  const openTooltip = () => {
    isOpen = true;

    computePosition(triggerRef, popoverRef, {
      placement: popoverPlacement,
      middleware: [
        flip(),
        shift({
          padding: 24
        }),
        offset(8)
      ]
    }).then(({ x, y }) => {
      Object.assign(popoverRef.style, {
        left: `${x}px`,
        top: `${y}px`
      });
    });

    setTimeout(() => {
      const firstFocusablePopoverEl: HTMLElement | null =
        popoverRef.querySelector(FOCUSABLE_ELS_SELECTOR);

      if (firstFocusablePopoverEl) {
        firstFocusablePopoverEl.focus();
      }
    }, ANIM_DURATION);
  };

  const handleHover = () => {
    isHovered = true;
    openTooltip();
  };

  const handleHoverOff = () => {
    isHovered = false;

    // Ensure its a true mouse off before closing to
    // prevent flicker.
    setTimeout(() => {
      if (!isHovered && isOpen) {
        closeTooltip();
      }
    }, 75);
  };

  const handleTriggerClick = () => {
    if (!isOpen && !isClosing) {
      openTooltip();
    }
  };

  const handleClickOutside = () => {
    if (isOpen) {
      closeTooltip();
    }
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      closeTooltip();
    }
  };

  const handleBlur = (event: FocusEvent) => {
    const target = event.relatedTarget as Node | null;
    // Check if the blur is going to an element outside the tooltip
    if (!popoverRef.contains(target)) {
      closeTooltip();
    }
  };

  const handlePopoverFocusIn = (event: FocusEvent) => {
    isFocusedWithin = true;
  };

  const handlePopoverFocusOut = (event: FocusEvent) => {
    setTimeout(() => {
      // Delay to check for focus shifting within the tooltip
      if (!popoverRef.contains(document.activeElement)) {
        isFocusedWithin = false;
        closeTooltip();
      }
    }, 0);
  };

  const setupListeners = () => {
    if (window.matchMedia('(hover: hover)').matches) {
      // Setup hover events for devices that support hover
      containerRef.addEventListener('mouseenter', handleHover);
      containerRef.addEventListener('mouseleave', handleHoverOff);
      containerRef.addEventListener('keydown', handleKeydown);
    } else {
      // Setup tap events for devices that do not support hover
      triggerRef.addEventListener('click', handleTriggerClick);
    }
  };

  onMount(() => {
    setupListeners();

    return () => {
      containerRef.removeEventListener('mouseenter', handleHover);
      containerRef.removeEventListener('mouseleave', handleHoverOff);
      containerRef.removeEventListener('keydown', handleKeydown);
      triggerRef.removeEventListener('click', handleTriggerClick);
    };
  });
</script>

<span class={twMerge('relative cursor-default', $$restProps.class)} bind:this={containerRef}>
  <button
    type="button"
    aria-label="More Info"
    class={twMerge('cursor-pointer font-medium text-blue', triggerClass)}
    on:focus={openTooltip}
    on:blur={handleBlur}
    bind:this={triggerRef}
    aria-haspopup="dialog"
    aria-expanded={isOpen}
    aria-describedby={popoverId}
    tabindex="-1"
  >
    <slot />
  </button>

  <span
    id={popoverId}
    bind:this={popoverRef}
    class={twMerge(
      'absolute left-0 top-0 z-30 min-w-[340px] rounded bg-white p-4 shadow-xl transition-[visibility,_opacity,_transform] md:p-6',
      animDurationClass,
      isOpen
        ? 'visible z-30 translate-y-0 scale-100 opacity-100'
        : 'invisible -z-10 -translate-y-5 scale-90 opacity-0',
      popoverClass
    )}
    role="tooltip"
    use:clickOutside={handleClickOutside}
    on:focusin={handlePopoverFocusIn}
    on:focusout={handlePopoverFocusOut}
  >
    <slot name="content">
      {textContent}
    </slot>
  </span>
</span>

<!--
@component
Wrap the content you wish to trigger the tooltip with this component. Set textContent for a text-only
tooltip, or pass custom content to the tooltip using the `content` slot.
## Props
@prop textContent: string = '';
@prop popoverClass: string = '';
@prop popoverPlacement: Placement = 'bottom';
## Slots
@slot `content`: custom content to show inside the tooltip popover.
-->
